// Track types

import { Vector2 } from './physics';

export interface Track {
  id: string;
  name: string;
  author: string;
  createdAt: number;
  updatedAt: number;
  difficulty: TrackDifficulty;
  defaultLapCount: number;
  width: number;
  height: number;
  // Infinite scroll: when enabled, cars wrap around to opposite edge
  wrapAround?: boolean;
  roadPath: RoadSegment[];
  boundaries: Boundary[];
  checkpoints: Checkpoint[];
  startFinishLine: FinishLine;
  spawnPoints: SpawnPoint[];
  elements: TrackElement[];
  scenery: SceneryItem[];
}

export type TrackDifficulty = 'easy' | 'medium' | 'hard' | 'extreme';

export interface RoadSegment {
  id: string;
  type: 'straight' | 'curve' | 'chicane';
  points: Vector2[];
  controlPoints?: Vector2[];
  width: number;
}

export interface Boundary {
  id: string;
  type: 'wall' | 'barrier' | 'grass';
  points: Vector2[];
  isClosed: boolean;
  // Layer for bridges/overpasses: 0 = ground, 1 = bridge, -1 = tunnel
  layer?: number;
}

export interface Checkpoint {
  id: string;
  index: number;
  position: Vector2;
  width: number;
  rotation: number;
}

export interface FinishLine {
  position: Vector2;
  width: number;
  rotation: number;
}

export interface SpawnPoint {
  index: number;
  position: Vector2;
  rotation: number;
}

// Simple track element types for editor and rendering
export type TrackElementType = 
  | 'road'
  | 'wall'
  | 'checkpoint'
  | 'finish'
  | 'boost'
  | 'oil'
  | 'spawn'
  | 'boost_pad'
  | 'ramp'
  | 'ramp_up'      // Transitions car to layer+1
  | 'ramp_down'    // Transitions car to layer-1
  | 'bridge'       // Road segment at elevated layer
  | 'oil_slick'
  | 'barrier'
  | 'tire_stack'
  | 'pit_stop';

export interface TrackElement {
  id: string;
  type: TrackElementType;
  // Direct position properties for simple access
  x: number;
  y: number;
  // Position object for backward compatibility
  position: Vector2;
  width: number;
  height: number;
  rotation: number;
  checkpointIndex?: number;
  // Layer for bridges/overpasses: 0 = ground, 1 = bridge, -1 = tunnel
  layer?: number;
  properties?: TrackElementProperties;
}

export interface TrackElementProperties {
  // Boost pad
  boostAmount?: number;
  boostDuration?: number;
  
  // Ramp
  launchAngle?: number;
  launchForce?: number;
  
  // Oil slick
  frictionMultiplier?: number;
  
  // Pit stop
  repairRate?: number;
  speedLimit?: number;
}

export interface SceneryItem {
  id: string;
  type: string;
  position: Vector2;
  rotation: number;
  scale: number;
}

export interface TrackMetadata {
  id: string;
  name: string;
  author: string;
  difficulty: TrackDifficulty;
  defaultLapCount: number;
  createdAt: number;
}

export interface TrackValidationResult {
  isValid: boolean;
  errors: TrackValidationError[];
  warnings: string[];
}

export interface TrackValidationError {
  code: string;
  message: string;
  element?: string;
}

// Default track template with minimum required elements
export const DEFAULT_TRACK: Track = {
  id: 'new-track',
  name: 'New Track',
  author: 'Unknown',
  createdAt: Date.now(),
  updatedAt: Date.now(),
  difficulty: 'easy',
  defaultLapCount: 5,
  width: 2000,
  height: 1200,
  roadPath: [
    {
      id: 'main-path',
      type: 'curve',
      points: [
        { x: 400, y: 600 },
        { x: 400, y: 300 },
        { x: 1000, y: 200 },
        { x: 1600, y: 300 },
        { x: 1600, y: 900 },
        { x: 1000, y: 1000 },
        { x: 400, y: 900 },
        { x: 400, y: 600 },
      ],
      width: 200,
    },
  ],
  boundaries: [
    {
      id: 'outer',
      type: 'wall',
      points: [
        { x: 200, y: 600 },
        { x: 200, y: 200 },
        { x: 1000, y: 50 },
        { x: 1800, y: 200 },
        { x: 1800, y: 1000 },
        { x: 1000, y: 1150 },
        { x: 200, y: 1000 },
        { x: 200, y: 600 },
      ],
      isClosed: true,
    },
    {
      id: 'inner',
      type: 'wall',
      points: [
        { x: 600, y: 600 },
        { x: 600, y: 400 },
        { x: 1000, y: 350 },
        { x: 1400, y: 400 },
        { x: 1400, y: 800 },
        { x: 1000, y: 850 },
        { x: 600, y: 800 },
        { x: 600, y: 600 },
      ],
      isClosed: true,
    },
  ],
  checkpoints: [
    { id: 'cp1', index: 0, position: { x: 1000, y: 125 }, width: 200, rotation: 0 },
    { id: 'cp2', index: 1, position: { x: 1700, y: 600 }, width: 200, rotation: Math.PI / 2 },
    { id: 'cp3', index: 2, position: { x: 1000, y: 1075 }, width: 200, rotation: Math.PI },
    { id: 'cp4', index: 3, position: { x: 300, y: 600 }, width: 200, rotation: -Math.PI / 2 },
  ],
  startFinishLine: {
    position: { x: 300, y: 700 },
    width: 200,
    rotation: -Math.PI / 2,
  },
  spawnPoints: [
    { index: 0, position: { x: 280, y: 600 }, rotation: -Math.PI / 2 },
    { index: 1, position: { x: 280, y: 680 }, rotation: -Math.PI / 2 },
    { index: 2, position: { x: 355, y: 600 }, rotation: -Math.PI / 2 },
    { index: 3, position: { x: 355, y: 680 }, rotation: -Math.PI / 2 },
  ],
  elements: [],
  scenery: [],
};
